"use client";

import EChartsBase from "@/app/components/EChartsBase";
import Loading from "@/app/components/Loading";
import { designColors } from "@/app/theme/theme";
import { getLatestPortfolioHistory } from "@/lib/portfolio";
import { Backdrop, Box, List, ListItem, ListItemText, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import PortfolioUpgrade from "./PortfolioUpgrade";
import { useTheme } from "@/app/theme/ThemeContext";

// 环形图颜色配置，按仓位大小分配颜色
const RingColors = ["#1DBD87", "#E8B325", "#A477C9", "#1D63F0", "#F67855", "#8665E5", "#01C6FA", "#F08737", "#69628A"];

/**
 * 获取币种图标URL
 * @param name 币种名称
 * @returns 图标文件路径
 */
function getIconUrl(name: string) {
  return name ? `/images/icon/${name.toLowerCase()}.png` : "";
}

/**
 * 格式化百分比，正数加"+ "，负数加"- "
 */
function formatPercent(val: string | number) {
  if (!val) return "0.00%";
  const num = Number(val);
  if (isNaN(num)) return "0.00%";
  if (num > 0) return `+ ${num}%`;
  if (num < 0) return `- ${Math.abs(num)}%`;
  return "0.00%";
}

/**
 * 投资组合页面主组件
 * 展示RightPulse高贝塔投资组合的收益信息、币种分布和详细说明
 */
export default function PortfolioComponent() {
  const theme = useTheme();
  const t = useTranslations("PortfolioComponent");
  const [portfolio, setPortfolio] = useState<any>(null);
  const [containerHeight, setContainerHeight] = useState<number | null>(null);
  const [maskTopOffset, setMaskTopOffset] = useState<number>(180);
  const portfolioPannelRef = useRef<HTMLDivElement>(null);
  const portfolioUpgradeRef = useRef<HTMLDivElement>(null);
  const dailyChangeRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 统一处理持仓数据，避免重复
  const sortedPortfolio =
    portfolio && Array.isArray(portfolio.portfolio)
      ? portfolio.portfolio.map((item: any, idx: number) => ({
          name: item.crypto,
          value: Number(item.percentage),
          color: RingColors[idx % RingColors.length],
        }))
      : [];

  /**
   * 生成环形图配置
   * 包含图标定位和图表样式设置
   */
  function getPieOption() {
    const pieData = sortedPortfolio;
    const total = pieData.reduce((a: any, b: any) => a + b.value, 0);
    let start = 90; // 起始角度
    const ringWidth = 280; // 环形图宽度
    const iconWidth = 24; // 图标大小

    // 只为仓位>=5%的币种显示图标，避免小扇区图标重叠
    const graphics = pieData
      .filter((item: any) => item.value >= 5)
      .map((item: any) => {
        // 计算图标在扇区中心的角度
        const angle = start - ((item.value / total) * 360) / 2;
        // 极坐标转笛卡尔坐标
        const rad = (angle * Math.PI) / 180;
        const r = ringWidth / 2 - iconWidth * 0.68; // 图标距离中心的半径
        const x = ringWidth / 2 + Math.cos(rad) * r - iconWidth / 2;
        const y = ringWidth / 2 - Math.sin(rad) * r - iconWidth / 2;
        start -= (item.value / total) * 360;

        return {
          type: "image",
          left: `${x}`,
          top: `${y}`,
          style: {
            image: getIconUrl(item.name),
            width: iconWidth,
            height: iconWidth,
          },
          z: 101,
        };
      });

    return {
      tooltip: {
        trigger: "item",
        formatter: (params: any) =>
          `<span style="display:inline-block;margin-right:8px;border-radius:50%;width:10px;height:10px;background:${
            params.color
          };"></span>
          ${params.name}: ${params.value.toFixed(2)}%`,
      },
      legend: { show: false },
      color: pieData.map((item: any) => item.color),
      series: [
        {
          name: "Portfolio",
          type: "pie",
          radius: ["79%", "98%"],
          padAngle: pieData.length > 1 ? 2 : 0,
          startAngle: 90,
          endAngle: -270,
          hoverAnimation: false,
          itemStyle: {
            borderRadius: 4,
          },
          label: {
            show: false,
          },
          data: pieData.map((item: any) => ({
            value: item.value,
            name: item.name,
            itemStyle: {
              color: item.color,
            },
          })),
          labelLine: { show: false },
        },
      ],
      graphic: graphics,
    };
  }

  async function loadLatestPortfolioHistory() {
    const result = await getLatestPortfolioHistory();
    setPortfolio(result.data);
  }

  useEffect(() => {
    loadLatestPortfolioHistory();
  }, []);

  // 动态计算容器高度
  const calculateHeight = () => {
    const portfolioPannelHeight = portfolioPannelRef.current?.offsetHeight || 0;
    const portfolioUpgradeHeight = portfolioUpgradeRef.current?.offsetHeight || 0;
    const totalHeight = portfolioPannelHeight + portfolioUpgradeHeight;

    // 计算 dailyChange 元素相对于最外层容器的位置
    let maskTopOffset = 180; // 默认值
    if (dailyChangeRef.current && containerRef.current) {
      const dailyChangeRect = dailyChangeRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      // 使用 dailyChange 元素底部相对于容器顶部的距离
      maskTopOffset = dailyChangeRect.top - containerRect.top - 8;
    }

    setContainerHeight(totalHeight);
    setMaskTopOffset(maskTopOffset);
  };

  // Backdrop 打开后的回调
  const handleBackdropEntered = () => {
    // Backdrop 动画完成后计算高度
    calculateHeight();
  };

  // 使用 ResizeObserver 监听元素大小变化
  useEffect(() => {
    if (!portfolio?.canAccess && portfolioUpgradeRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        calculateHeight();
      });

      // 监听 PortfolioUpgrade 元素的大小变化
      resizeObserver.observe(portfolioUpgradeRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [portfolio?.canAccess]);

  // 重置高度当有权限时
  useEffect(() => {
    if (portfolio?.canAccess) {
      setContainerHeight(null);
    }
  }, [portfolio?.canAccess]);

  if (!portfolio) {
    return <Loading />;
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        maxWidth: { md: "48rem" },
        mx: "auto",
        px: { xs: 3, sm: 2 },
        py: { xs: 3, sm: 7 },
        pb: { xs: 5, sm: 16 },
        position: "relative",
        minHeight: containerHeight ? `${containerHeight}px` : "auto",
      }}>
      <Box>
        <Box ref={portfolioPannelRef}>
          {/* 标题和更新时间 */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: { xs: "flex-start", sm: "space-between" },
              alignItems: { xs: "flex-start", sm: "center" },
              mb: { xs: 2.7, sm: 5 },
            }}>
            <Typography
              variant="h5"
              fontWeight={600}
              sx={{
                fontSize: "var(--font-size-xl)",
                color: "text.primary",
                mb: { xs: 1, sm: 0 },
              }}>
              {t("title")}
            </Typography>
            {/* 更新时间 */}
            <Typography
              variant="body2"
              sx={{
                color: "text.tertiary",
                fontSize: "var(--font-size-xs)",
              }}>
              {t("updated", { date: dayjs().format("YYYY/MM/DD") })}
            </Typography>
          </Box>

          {/* 收益指标展示 */}
          <Box
            sx={{
              display: "flex",
              gap: { xs: 0, sm: 14 },
              mb: { xs: 4, sm: 8 },
            }}>
            <Box sx={{ flexGrow: { xs: 1, sm: 0 } }}>
              <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "var(--font-size-xs)", mb: 0.5 }}>
                {t("yieldLabel")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "var(--font-size-xl)",
                  fontWeight: 600,
                  color: designColors.green[500],
                  lineHeight: 1.1,
                }}
                ref={dailyChangeRef}
                >
                {/* 临时调整mask高度，应对收益率错误。dailyChangeRef原来是id="dailyChange"的组件上 */}
                {formatPercent(portfolio?.totalYield)}
              </Typography>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mt: "0.75rem" }}
                id="dailyChange"
                >
                <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "var(--font-size-xs)" }}>
                  {t("24hourChangeLabel")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: portfolio?.change24h > 0 ? designColors.green[500] : designColors.red[500],
                    fontSize: "var(--font-size-xs)",
                    fontWeight: 600,
                    lineHeight: 1.6,
                  }}>
                  {formatPercent(portfolio?.change24h)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flexGrow: { xs: 1, sm: 0 } }}>
              <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "var(--font-size-xs)", mb: 0.5 }}>
                {t("annualizedLabel")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "var(--font-size-xl)",
                  fontWeight: 600,
                  color: designColors.green[500],
                  lineHeight: 1.1,
                }}>
                {formatPercent(portfolio?.annualizedYield)}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: "0.75rem" }}>
                <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "var(--font-size-xs)" }}>
                  {t("30dayChangeLabel")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: portfolio?.change30d > 0 ? designColors.green[500] : designColors.red[500],
                    fontSize: "var(--font-size-xs)",
                    fontWeight: 600,
                    lineHeight: 1.6,
                  }}>
                  {formatPercent(portfolio?.change30d)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* 环形图与币种分布列表区域 */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "stretch",
            justifyContent: "space-between",
            mb: { xs: 2, sm: 10 },
          }}>
          {/* 环形图容器 */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <Box sx={{ width: "17.5rem", height: "17.5rem" }}>
              <EChartsBase option={getPieOption()} style={{ width: "100%", height: "100%" }} />
            </Box>
          </Box>

          {/* 币种分布列表 */}
          <Box
            sx={{
              maxWidth: { xs: "100%", sm: "19.5rem" },
              width: { xs: "100%", md: "50%" },
              ml: { sm: 4, md: 0 },
              mt: { xs: 4, sm: 0 },
            }}>
            {/* 列表标题 */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
              <Typography variant="subtitle1" sx={{ fontSize: "var(--font-size-s)", color: "text.secondary" }}>
                {t("currency")}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: "var(--font-size-s)",
                  color: "text.secondary",
                }}>
                {t("positionPercentage")}
              </Typography>
            </Box>
            {/* 币种列表项 */}
            <List>
              {sortedPortfolio.map((item: any) => (
                <ListItem
                  key={item.name}
                  sx={{
                    py: 0.5,
                    minHeight: 36,
                    display: "flex",
                    alignItems: "center",
                    px: 0,
                  }}>
                  {/* 颜色指示器 */}
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      bgcolor: item.color,
                      mr: 2,
                      flexShrink: 0,
                    }}
                  />
                  {/* 币种名称 */}
                  <ListItemText
                    primary={
                      <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "text.primary" }}>
                        {item.name}
                      </Typography>
                    }
                  />
                  {/* 仓位百分比 */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "text.primary",
                    }}>
                    {(item.value?.toFixed(2) || "0.00") + "%"}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </Box>
      {/* 投资组合详细说明区域 */}
      {portfolio?.canAccess && (
        <Box>
          {/* 投资组合概览 */}
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: "var(--font-size-l)", mb: 1.5 }}>
            {t("overviewTitle")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, fontSize: "1rem" }}>
            {t("overviewDesc")}
          </Typography>

          {/* 动态再平衡说明 */}
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ fontSize: "var(--font-size-l)", mb: 1.5 }}>
            {t("rebalanceTitle")}
          </Typography>

          {/* 持续进化 */}
          <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: "1rem" }}>
            {t("evolutionTitle")}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2.5, fontSize: "1rem" }}>
            {t("evolutionDesc")}
          </Typography>

          {/* 如何跟随 */}
          <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: "1rem" }}>
            {t("howToFollowTitle")}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2.5, fontSize: "1rem" }}>
            {t("howToFollowDesc")}
          </Typography>

          {/* 完全透明 */}
          <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: "1rem" }}>
            {t("transparencyTitle")}
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, fontSize: "1rem" }}>
            {t.rich("transparencyDesc", { br: () => <br /> })}
          </Typography>
          {/* 风险披露 */}
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: "var(--font-size-l)", mb: 1.5 }}>
            {t("riskTitle")}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: "1rem" }}>
            {t.rich("riskDesc", { br: () => <br /> })}
          </Typography>
        </Box>
      )}
      {/* 没有访问权限 显示遮罩 */}
      <Backdrop
        open={!portfolio?.canAccess}
        onEntered={handleBackdropEntered}
        sx={{
          position: "absolute",
          top: maskTopOffset,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10,
          backdropFilter: "blur(6px)",
          background: theme.isDarkMode
            ? "linear-gradient(180deg, rgba(22, 26, 30, 0.00) 0%, #161A1E 24.28%, #161A1E 100%)"
            : "linear-gradient(180deg, rgba(255, 250, 245, 0.00) 0%, #FFFAF5 24.28%, #FFFAF5 100%)",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          px: 2,
          pt: 0,
        }}>
        <Box
          ref={portfolioUpgradeRef}
          sx={{
            maxWidth: "48rem",
            width: "100%",
            overflowY: "auto",
          }}>
          <PortfolioUpgrade />
        </Box>
      </Backdrop>
    </Box>
  );
}
